import { useTranslation } from "react-i18next";
import { type Theme, useTheme } from "../hooks/useTheme";

interface ThemeToggleProps {
  compact?: boolean;
}

export function ThemeToggle({ compact = false }: ThemeToggleProps) {
  const { theme, setTheme } = useTheme();
  const { t } = useTranslation();

  if (compact) {
    return (
      <button
        type="button"
        onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
        aria-label={t("theme.toggle", { defaultValue: "Theme wechseln" })}
        title={t("theme.toggle", { defaultValue: "Theme wechseln" })}
        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted hover:bg-row-hover hover:text-text"
        style={{ background: "transparent" }}
      >
        {theme === "dark" ? <MoonIcon /> : <SunIcon />}
      </button>
    );
  }

  return (
    <div
      role="group"
      aria-label={t("theme.label", { defaultValue: "Theme" })}
      className="inline-flex items-center gap-0.5 rounded-full p-0.5"
      style={{ background: "var(--card)", border: "1px solid var(--border)" }}
    >
      <ThemeButton
        active={theme === "light"}
        onClick={() => setTheme("light")}
        label={t("theme.light", { defaultValue: "Light" })}
        icon={<SunIcon />}
      />
      <ThemeButton
        active={theme === "dark"}
        onClick={() => setTheme("dark")}
        label={t("theme.dark", { defaultValue: "Dark" })}
        icon={<MoonIcon />}
      />
    </div>
  );
}

function ThemeButton({
  active,
  onClick,
  label,
  icon,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  icon: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors"
      style={{
        background: active ? "var(--accent-soft)" : "transparent",
        color: active ? "var(--accent)" : "var(--muted)",
      }}
    >
      {icon}
      {label}
    </button>
  );
}

function SunIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 12.8A9 9 0 1111.2 3a7 7 0 009.8 9.8z" />
    </svg>
  );
}

export type { Theme };
